import { useState, useEffect, useMemo } from 'react'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { crewSpans, dayStatus } from '../lib/rotationGrid'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

function getVal(v) { return (v && typeof v === 'object' && 'value' in v) ? v.value : (v ?? '') }

export default function RotationGrid() {
  const [crew, setCrew] = useState([])
  const [blocks, setBlocks] = useState([])
  const [loading, setLoading] = useState(true)
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth())
  const [pick, setPick] = useState(null) // { name, crewId, dateISO }
  const [pickDays, setPickDays] = useState(1)
  const [saving, setSaving] = useState(false)
  const [paint, setPaint] = useState(null) // { name, crewId, ri, startDay, endDay }
  useEffect(() => {
    if (!paint) return
    const up = () => {
      setPaint(cur => {
        if (cur) {
          const from = Math.min(cur.startDay, cur.endDay)
          const to = Math.max(cur.startDay, cur.endDay)
          setPick({ name: cur.name, crewId: cur.crewId, dateISO: iso(from) })
          setPickDays((to - from) + 1)
        }
        return null
      })
    }
    window.addEventListener('mouseup', up)
    return () => window.removeEventListener('mouseup', up)
  }, [paint])

  function dcount(a, b) { const d1 = new Date(a + 'T12:00:00'), d2 = new Date(b + 'T12:00:00'); return (isNaN(d1) || isNaN(d2)) ? 0 : Math.max(0, Math.round((d2 - d1) / 86400000) + 1) }
  function addDaysISO(iso0, n) { const d = new Date(iso0 + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0] }

  async function setStatus(type) {
    if (!pick) return
    setSaving(true)
    try {
      const start = pick.dateISO
      const end = addDaysISO(start, Math.max(1, Number(pickDays) || 1) - 1)
      // Trim any existing blocks for this person that overlap the new range (simple split-free trim).
      const overlaps = (blocks || []).filter(b => {
        const bn = String(b['Crew Name'] || '').toLowerCase()
        const nm = pick.name.toLowerCase()
        const nameOk = bn === nm || (bn.includes(nm.split(' ')[0]) && bn.includes(nm.split(' ').pop()))
        if (!nameOk) return false
        return !(String(b['End Date']) < start || String(b['Start Date']) > end)
      })
      for (const b of overlaps) {
        const bs = String(b['Start Date']), be = String(b['End Date'])
        if (bs >= start && be <= end) { await updateRow('crew_leave', b.id, { 'End Date': bs, 'Days': 0, 'Leave Type': (b['Leave Type']||'Leave'), 'Notes': 'superseded' }) }
        else if (bs < start && be > end) { await updateRow('crew_leave', b.id, { 'End Date': addDaysISO(start, -1), 'Days': dcount(bs, addDaysISO(start, -1)) }) }
        else if (bs < start) { await updateRow('crew_leave', b.id, { 'End Date': addDaysISO(start, -1), 'Days': dcount(bs, addDaysISO(start, -1)) }) }
        else if (be > end) { await updateRow('crew_leave', b.id, { 'Start Date': addDaysISO(end, 1), 'Days': dcount(addDaysISO(end, 1), be) }) }
      }
      await createRow('crew_leave', {
        'Crew ID': pick.crewId || '',
        'Crew Name': pick.name,
        'Leave Type': type,
        'Start Date': start,
        'End Date': end,
        'Days': dcount(start, end),
        'Notes': 'set on grid',
      })
      setPick(null); setPickDays(1)
      load()
    } catch (e) { alert('Could not set status: ' + e.message) }
    finally { setSaving(false) }
  }

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const [c, b] = await Promise.all([fetchRows('crew'), fetchRows('crew_leave')])
      const active = (c || []).filter(r => !(r['Archived'] === true || String(r['Archived']) === 'true'))
      setCrew(active)
      setBlocks(b || [])
    } catch (e) { setCrew([]); setBlocks([]) }
    finally { setLoading(false) }
  }

  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)
  function iso(d) { return year + '-' + String(month + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0') }

  // each crew member's leave rows, matched once per load (not per cell and render - audit-100 item 5)
  const nameOf = c => (getVal(c['First Name']) + ' ' + getVal(c['Last Name'])).trim()
  const spans = useMemo(() => crewSpans(crew.map(nameOf), blocks), [crew, blocks])
  const todayStr = new Date().toISOString().split('T')[0]
  function statusFor(name, dayISO, crewRow) {
    const onboard = !!crewRow && (crewRow['Onboard'] === true || String(crewRow['Onboard']) === 'true')
    return dayStatus(spans.get(name), dayISO, onboard, todayStr)
  }

  function shift(delta) {
    let m = month + delta, y = year
    if (m < 0) { m = 11; y-- } else if (m > 11) { m = 0; y++ }
    setMonth(m); setYear(y)
  }

  const todayISO = new Date().toISOString().split('T')[0]

  return (
    <div className="tbl-wrap fade-up">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn" onClick={() => shift(-1)} style={{ fontSize: 13, padding: '6px 12px' }}>&#8249;</button>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', minWidth: 170, textAlign: 'center' }}>{MONTHS[month]} {year}</div>
        <button className="btn" onClick={() => shift(1)} style={{ fontSize: 13, padding: '6px 12px' }}>&#8250;</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: 11, color: 'var(--muted)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(93,186,142,.5)', marginRight: 5 }} />Working</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(224,112,112,.5)', marginRight: 5 }} />Leave</span>
        </div>
      </div>
      {loading && <div className="loading-row"><div className="spin" />Loading...</div>}
      {/* iPad (600-1279px): the days of the month share the width (fixed layout, name and position on two lines) - no
          sideways scroll (ipad) */}
      <style>{`
        @media (min-width: 600px) and (max-width: 1279px) {
          /* a real table again (the phone shell turns tables into scrolling blocks), so the fixed layout applies */
          .hc-shell .hc-content .hc-rot-grid table { display: table !important; overflow: visible !important; width: 100% !important; table-layout: fixed; }
          .hc-rot-grid thead th:first-child { width: 112px; min-width: 0 !important; padding: 6px 6px !important; }
          .hc-rot-grid th, .hc-rot-grid td { min-width: 0 !important; }
          .hc-rot-grid thead th:not(:first-child), .hc-rot-grid tbody td:not(:first-child) { width: auto !important; }
          .hc-rot-grid tbody td:first-child { white-space: normal !important; padding: 5px 6px !important; overflow-wrap: break-word; }
          .hc-rot-grid tbody td:first-child span { display: block; margin-left: 0 !important; }
        }
      `}</style>
      {!loading && (
        <div className="hc-rot-grid" style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--ink2)', zIndex: 2, textAlign: 'left', padding: '6px 10px', minWidth: 150, borderBottom: '0.5px solid var(--border)', color: 'var(--muted)', fontSize: 10 }}>Crew</th>
                {days.map(d => {
                  const dow = new Date(year, month, d).getDay()
                  const wknd = dow === 0 || dow === 6
                  return <th key={d} style={{ padding: '4px 0', width: 22, textAlign: 'center', borderBottom: '0.5px solid var(--border)', color: iso(d) === todayISO ? 'var(--gold2)' : (wknd ? 'var(--muted)' : 'var(--text)'), fontWeight: iso(d) === todayISO ? 700 : 500, background: wknd ? 'rgba(255,255,255,.03)' : 'transparent' }}>{d}</th>
                })}
              </tr>
            </thead>
            <tbody>
              {crew.map((c, ri) => {
                const name = (getVal(c['First Name']) + ' ' + getVal(c['Last Name'])).trim()
                return (
                  <tr key={c.id || ri}>
                    <td style={{ position: 'sticky', left: 0, background: 'var(--hull)', zIndex: 1, padding: '5px 10px', whiteSpace: 'nowrap', borderBottom: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 11 }}>
                      {name}<span style={{ color: 'var(--muted)', fontSize: 9, marginLeft: 6 }}>{getVal(c['Position'])}</span>
                    </td>
                    {days.map(d => {
                      const st = statusFor(name, iso(d), c)
                      const bg = st === 'W' ? 'rgba(93,186,142,.45)' : st === 'L' ? 'rgba(224,112,112,.45)' : 'transparent'
                      const isToday = iso(d) === todayISO
                      const inPaint = paint && paint.ri === ri && d >= Math.min(paint.startDay, paint.endDay) && d <= Math.max(paint.startDay, paint.endDay)
                      return <td key={d} onMouseDown={() => { setPaint({ name, crewId: getVal(c['Crew ID']) || '', ri, startDay: d, endDay: d }) }} onMouseEnter={() => { setPaint(cur => cur && cur.ri === ri ? { ...cur, endDay: d } : cur) }} title={(st === 'W' ? 'Working' : st === 'L' ? 'Leave' : 'Set') + ' \u00b7 click or drag to edit'} style={{ width: 22, height: 22, background: inPaint ? 'rgba(212,175,110,.6)' : bg, borderBottom: '0.5px solid var(--border)', borderLeft: isToday ? '1px solid var(--gold2)' : 'none', borderRight: isToday ? '1px solid var(--gold2)' : 'none', boxShadow: inPaint ? 'inset 0 0 0 1px var(--gold2)' : 'none', cursor: 'pointer', userSelect: 'none' }} />
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && crew.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 40 }}>No crew to show.</div>}
      {pick && (
        <div onClick={() => setPick(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ink2)', border: '0.5px solid var(--border)', borderRadius: 14, padding: 22, width: 320, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{pick.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>From <b style={{ color: 'var(--text)' }}>{pick.dateISO}</b></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--muted)' }}>for
              <input type="number" min={1} value={pickDays} onChange={e => setPickDays(e.target.value)} style={{ width: 60, background: 'var(--hull)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '5px 8px', borderRadius: 6, fontFamily: 'inherit' }} /> days
            </div>
            <div style={{ display: 'flex', gap: 9 }}>
              <button onClick={() => setStatus('Working')} disabled={saving} style={{ flex: 1, background: 'rgba(93,186,142,.9)', border: 'none', color: '#06281a', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '10px', borderRadius: 8 }}>Working</button>
              <button onClick={() => setStatus('Leave')} disabled={saving} style={{ flex: 1, background: 'rgba(224,112,112,.9)', border: 'none', color: '#3a0d0d', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '10px', borderRadius: 8 }}>Leave</button>
            </div>
            <button onClick={() => setPick(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
